import React from "react";
import { Helmet } from "react-helmet-async";
import { BUSINESS } from "@/data/config";

// Small, deterministic SEO wrapper so every page gets its own title, meta, canonical, JSON-LD.
export default function SEO({
  title,
  description,
  path = "/",
  image,
  jsonLd,
  noindex = false,
}) {
  const url = `${BUSINESS.siteUrl}${path === "/" ? "" : path}`.replace(/\/$/, "") || BUSINESS.siteUrl;
  const canonical = url || BUSINESS.siteUrl;
  const ogImage =
    image ||
    "https://images.unsplash.com/photo-1778856951796-d1192dab6d54?crop=entropy&cs=srgb&fm=jpg&w=1200";

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex ? <meta name="robots" content="noindex,nofollow" /> : <meta name="robots" content="index,follow,max-image-preview:large" />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BUSINESS.name} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export function buildLocalBusinessJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": `${BUSINESS.siteUrl}#business`,
    name: BUSINESS.name,
    url: BUSINESS.siteUrl,
    telephone: BUSINESS.phone,
    email: BUSINESS.email,
    address: {
      "@type": "PostalAddress",
      addressLocality: BUSINESS.addressLocality,
      addressRegion: BUSINESS.addressRegion,
      postalCode: BUSINESS.postalCode,
      addressCountry: "US",
    },
    areaServed: BUSINESS.areaServed.map((a) => ({ "@type": "City", name: a })),
    openingHoursSpecification: BUSINESS.hours
      .filter((h) => h.open !== "Closed")
      .map((h) => ({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: h.day,
        opens: h.open,
        closes: h.close,
      })),
  };
}

export function buildServiceJsonLd(service, city) {
  const areaServed = city
    ? { "@type": "City", name: `${city.name}, WA` }
    : BUSINESS.areaServed.map((a) => ({ "@type": "City", name: a }));
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: city ? `${service.name} in ${city.name}, WA` : service.name,
    provider: {
      "@type": "RoofingContractor",
      name: BUSINESS.name,
      telephone: BUSINESS.phone,
      url: BUSINESS.siteUrl,
    },
    areaServed,
    description: service.short,
  };
}

export function buildBreadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${BUSINESS.siteUrl}${it.path}`,
    })),
  };
}
